import { match } from 'ts-pattern'
import { Primary13SemiBold, Box } from '@sequencehq/core-components'
import type { ConditionalProperty } from 'Automations/nodeDefinitions/conditions'
import {
  productBillingFrequency,
  productBillingType,
  productDiscount,
  productHasDiscount,
  productPricingModel
} from 'Automations/nodeDefinitions/conditions/helpers/functionStrings'
import { semanticTypes } from 'Automations/nodeDefinitions/semanticTypes'
import {
  formatProductBillingFrequency,
  formatProductBillingType,
  formatProductDiscount,
  formatProductName,
  formatProductPricingModel
} from '../../../../switch/components/GroupEditor/inputs'
import { ProductPropertyEditor } from './ProductPropertyEditor'
import {
  validateProductBillingFrequencyValue,
  validateProductBillingTypeValue,
  validateProductDiscountValue,
  validateProductHasDiscountValue,
  validateProductPricingModelValue
} from './product.validators'

export const productConditionalProperty: ConditionalProperty = {
  label: 'Product',
  // Surfaces the chosen product's name between the property label and
  // the operator, so each operator's `formatValue` can stay focused on
  // its own comparison (e.g. DISCOUNT's value formatter just renders
  // "equals 25%").
  formatPreviewProperty: formatProductName,
  // The property editor renders its own product picker first, so the
  // framework operator dropdown isn't shown — auto-opening it would be
  // wasted UI.
  focusOperatorOnSelect: false,
  // PRODUCT owns the whole editing UI in one component so all
  // cross-operator rules (which operators are available given sibling
  // rows on the same product, sub-operator filtering for DISCOUNT, etc.)
  // live in one place.
  propertyEditorComponent: ProductPropertyEditor,
  // Always available: a switch can hold multiple PRODUCT rows targeting
  // different products. The editor itself blocks any in-product
  // redundancy.
  propertyAvailable: () => true,
  validateProperty: ({ operator, value }) =>
    match(operator)
      .with('DISCOUNT', () => validateProductDiscountValue({ value }))
      .with('HAS_DISCOUNT', () => validateProductHasDiscountValue({ value }))
      .with('PRICING_MODEL', () => validateProductPricingModelValue({ value }))
      .with('BILLING_FREQUENCY', () =>
        validateProductBillingFrequencyValue({ value })
      )
      .with('BILLING_TYPE', () => validateProductBillingTypeValue({ value }))
      .otherwise(() => undefined),
  // Each operator declares its own `valueSemanticType`; PRODUCT itself
  // doesn't set a property-level default since its operators read
  // different upstream values. An operator is hidden from the dropdown
  // unless its semantic type is present in the upstream outputs, and
  // the property is hidden entirely if every one of its operators is
  // hidden.
  operators: {
    DISCOUNT: {
      label: 'Discount',
      valueSemanticType: semanticTypes.sequence.quote.productDiscounts,
      functionString: productDiscount({
        currencySemanticType: semanticTypes.sequence.quote.currency
      }),
      formatPreviewValue: formatProductDiscount
    },
    HAS_DISCOUNT: {
      label: 'Has discount',
      valueSemanticType: semanticTypes.sequence.quote.productDiscounts,
      functionString: productHasDiscount,
      // The clause "has a discount" / "doesn't have a discount" is
      // produced entirely by `formatPreviewValue`; the operator label
      // is suppressed so the canvas reads naturally.
      formatPreviewOperator: () => '',
      formatPreviewValue: ({ value }) => (
        <Box as="span" {...Primary13SemiBold}>
          {value?.hasDiscount === false
            ? 'does not have a discount'
            : 'has a discount'}
        </Box>
      )
    },
    PRICING_MODEL: {
      label: 'Pricing model',
      valueSemanticType: semanticTypes.sequence.quote.phases,
      // The function string reads phases off `property` (resolved from
      // this operator's `valueSemanticType` above); the runtime helper
      // iterates `phases[].prices[]` and collects `{ pricingType,
      // hasTiers }` for every price matching the selected product.
      functionString: productPricingModel,
      formatPreviewValue: formatProductPricingModel
    },
    BILLING_FREQUENCY: {
      label: 'Billing frequency',
      valueSemanticType: semanticTypes.sequence.quote.phases,
      // Like PRICING_MODEL, this reads phases off `property` (resolved from
      // this operator's `valueSemanticType` above); the runtime helper walks
      // `phases[].prices[]` and collects the billing frequency of every price
      // matching the selected product.
      functionString: productBillingFrequency,
      formatPreviewValue: formatProductBillingFrequency
    },
    BILLING_TYPE: {
      label: 'Billing type',
      valueSemanticType: semanticTypes.sequence.quote.phases,
      // Reads phases off `property` the same way as the two operators above;
      // the runtime helper walks `phases[].prices[]` and collects the billing
      // type of every price matching the selected product.
      functionString: productBillingType,
      formatPreviewValue: formatProductBillingType
    }
  }
}
